import { AnyAction } from 'redux';
import {
  ANZAHL_TAGE_TIER_ZU_JUNG,
  App,
  determineLaufzeit,
  getVetragsOptionenFromVorvertrag,
  isTierZuJungByToday,
  TierartEnum,
  TkvV2Vorbelegungdaten
} from '@ps20-frontend/frontend-models';
import {
  GlobalActions,
  TARIF_CHECKED,
  VereinbarungenChanged,
  VorbelegungPayload
} from '@ps20-frontend/shop-integration-redux';
import { addVorvertragToAngeboteReducer, vertragsoptionenReducer } from '@ps20-frontend/reducers';
import cloneDeep from 'lodash/cloneDeep';
import { Selbstbeteiligung, TaasTarifrisikoschluesselTKV_V2 } from '@ps20/bff-models';
import { DATEFORMAT_LOCAL_STATE } from '@ps20-frontend/utils';
import dayjs from 'dayjs';
import { TkvActions } from '../tkvActions';
import { IAppState } from '../appState';
import { CoverageType, VertragsoptionenTKV } from '../models/vertragsoptionen.model';

export function mapTierartAufSelbstbeteiligung(
  tierart: TierartEnum,
  tkvSelbstBehaltFT = false
): Selbstbeteiligung {
  return {
    [TierartEnum.HUND]: Selbstbeteiligung.SELBSTBETEILIGUNG_20_PROZENT,
    [TierartEnum.KATZE]: Selbstbeteiligung.SELBSTBETEILIGUNG_20_PROZENT,
    [TierartEnum.PFERD]: tkvSelbstBehaltFT
      ? Selbstbeteiligung.SELBSTBETEILIGUNG_20_PROZENT
      : Selbstbeteiligung.SELBSTBETEILIGUNG_10_PROZENT
  }[tierart];
}

function animalBirthDateChanged(
  sectionState: VertragsoptionenTKV,
  action: AnyAction,
): VertragsoptionenTKV {
  const newState: VertragsoptionenTKV = cloneDeep(sectionState);
  const geburtsdatumTier: string = action.payload;
  if (isTierZuJungByToday(geburtsdatumTier, DATEFORMAT_LOCAL_STATE)) {
    newState.beginnDatum = dayjs(geburtsdatumTier, DATEFORMAT_LOCAL_STATE)
      .add(ANZAHL_TAGE_TIER_ZU_JUNG, 'day')
      .format(DATEFORMAT_LOCAL_STATE);
  } else {
    newState.beginnDatum = dayjs().add(1, 'd').format(DATEFORMAT_LOCAL_STATE);
  }
  return newState;
}

function vorbelegungForBeibehaltung(
  sectionState: VertragsoptionenTKV,
  action: AnyAction,
  state: IAppState,
): VertragsoptionenTKV {
  const actionPayload: TkvV2Vorbelegungdaten = action.payload;
  if (actionPayload.forApp === App.TKV_V2 && actionPayload.angebotsId === state.angebotsId) {
    return {
      ...getVetragsOptionenFromVorvertrag(sectionState, actionPayload),
      coverageType: actionPayload.vorvertragBausteinList.some(
        elem => elem.taasTarifrisikoschluessel === TaasTarifrisikoschluesselTKV_V2.HB) ? CoverageType.VOLLSCHUTZ : CoverageType.OPSCHUTZ,
      hasWechselbaustein: actionPayload.vorvertragBausteinList.some(
        elem => elem.taasTarifrisikoschluessel === TaasTarifrisikoschluesselTKV_V2.TW)
    };
  }
  return sectionState;
}

function vorbelegungRequested(
  sectionState: VertragsoptionenTKV,
  action: AnyAction,
  state: IAppState,
): VertragsoptionenTKV {
  const actionPayload: VorbelegungPayload = action.payload;
  const { appId, angebotsId } = actionPayload;
  if (
    appId === App.TKV_V2 &&
    angebotsId === state.angebotsId &&
    actionPayload.vorhandeneVorbelegungdaten
  ) {
    const tkvVorbelegungdaten: TkvV2Vorbelegungdaten =
      actionPayload.vorhandeneVorbelegungdaten as TkvV2Vorbelegungdaten;

    const vorvertragsNummern = sectionState.vorvertragsNummern;
    if (vorvertragsNummern.length > 0) {
      vorvertragsNummern[0] = {
        ...vorvertragsNummern[0],
        selbstbeteiligung: tkvVorbelegungdaten.selbstbeteiligung,
        verkaufsangebotKurzname: tkvVorbelegungdaten.verkaufsangebotKurzname,
        vorbelegungForBeibehaltung: {
          ...vorvertragsNummern[0].vorbelegungForBeibehaltung,
          preisverzeichnis: tkvVorbelegungdaten.preisverzeichnis
        },
      };
    }
    const fullcasco = tkvVorbelegungdaten.vorvertragBausteinList.some(
      elem => elem.taasTarifrisikoschluessel === TaasTarifrisikoschluesselTKV_V2.HB);
    const coverageType = fullcasco ? CoverageType.VOLLSCHUTZ : CoverageType.OPSCHUTZ;
    return {
      ...sectionState,
      vorvertragsNummern,
      urbeginn: tkvVorbelegungdaten.urbeginn,
      verkaufsangebotKurzname: tkvVorbelegungdaten.verkaufsangebotKurzname,
      selbstbeteiligung: tkvVorbelegungdaten.selbstbeteiligung ?? sectionState.selbstbeteiligung,
      hasWechselbaustein: tkvVorbelegungdaten.vorvertragBausteinList.some(
        elem => elem.taasTarifrisikoschluessel === TaasTarifrisikoschluesselTKV_V2.TW
      ),
      coverageType
    };
  }
  return sectionState;
}

export const tkvVertragsoptionenReducer = function(
  sectionState: VertragsoptionenTKV = null,
  action: AnyAction,
  state: IAppState
): VertragsoptionenTKV {
  sectionState = vertragsoptionenReducer(
    sectionState,
    action,
    App.TKV_V2,
    state
  ) as VertragsoptionenTKV;
  switch (action.type) {
    case TkvActions.ANIMAL_TYPE_CHANGED: {
      return { ...sectionState, coverageType: CoverageType.OPSCHUTZ };
    }
    case TkvActions.ANIMAL_BIRTH_DATE_CHANGED:
      return animalBirthDateChanged(sectionState, action);
    case TkvActions.COVERAGE_TYPE_CHANGED: {
      const coverageType: CoverageType = action.payload;
      return { ...sectionState, coverageType };
    }
    case TkvActions.SELBSTBETEILIGUNG_CHANGED: {
      const selbstbeteiligung: Selbstbeteiligung = action.payload;
      return { ...sectionState, selbstbeteiligung };
    }
    case TARIF_CHECKED: {
      const newState: VertragsoptionenTKV = cloneDeep(sectionState);
      determineLaufzeit(action, newState, state.produkt, sectionState);
      return newState;
    }
    case GlobalActions.ADD_VORVERTRAG_TO_ANGEBOTE: {
      return addVorvertragToAngeboteReducer(
        sectionState,
        action,
        App.TKV_V2,
        state.angebotsId
      ) as VertragsoptionenTKV;
    }
    case GlobalActions.VEREINBARUNGEN_CHANGED: {
      const newState: VertragsoptionenTKV = cloneDeep(sectionState);
      const actionPayload: VereinbarungenChanged = action.payload;
      const kuendigungenPayload = actionPayload.kuendingungChanged.some(elem => {
        return elem.appId === App.TKV_V2 && elem.angebotsId === state.angebotsId;
      });
      if (kuendigungenPayload) {
        newState.vorvertragGekuendigt = true;
      }
      return newState;
    }
    case GlobalActions.VORBELEGUNG_FOR_BEIBEHALTUNG_REQUESTED:
      return vorbelegungForBeibehaltung(sectionState, action, state);

    case GlobalActions.VORBELEGUNG_REQUESTED:
      return vorbelegungRequested(sectionState, action, state);
    default:
      return sectionState;
  }
};
